<template>
  <div
      class="base-loader"
      :class="{
        absolute,
        'no-shadow': noShadow,
        'no-background': noBackground,
      }"
  >
    <div :style="minHeightPx">
      <div>
        <div class="base-loader__content">
          <div>
            <FontAwesomeIcon icon="fa-light fa-spinner" class="fa-pulse" />
          </div>
          <div>
            <slot></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  onMounted,
  onBeforeUnmount,
  reactive,
  computed,
} from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';

library.add(faSpinner);

const props = withDefaults(defineProps<{
  absolute?: boolean
  noShadow?: boolean
  noBackground?: boolean
  color?: string
}>(), {
  color: '#38383A',
});

const header = reactive({
  $el: null as null | HTMLElement,
});
const minHeight = reactive({ value: null as number | null });
const minHeightPx = computed(() => (minHeight.value ? `min-height:${minHeight.value}px` : undefined));

const setMinHeight = () => {
  minHeight.value = window.innerHeight;

  if (header.$el) {
    minHeight.value -= header.$el.offsetHeight;
  }
};

onMounted(() => {
  header.$el = document.querySelector('header');

  if (props.absolute) return;

  window.addEventListener('resize', setMinHeight);
  window.addEventListener('load', setMinHeight);
  setMinHeight();
  for (let i = 100; i < 3000; i += 100) {
    setTimeout(setMinHeight, i);
  }
});

onBeforeUnmount(() => {
  if (props.absolute) return;

  window.removeEventListener('resize', setMinHeight);
  window.removeEventListener('load', setMinHeight);
});
</script>

<style lang="scss">
@import '../scss/component-helpers';

.base-loader {
  &:not(.no-background) {
    background: #F7F7F7;

    &.absolute {
      background: transparentize(#F7F7F7, .5);
    }
  }

  &:not(.no-shadow) {
    box-shadow: 0 0 6px inset rgba(0, 0, 0, .16);
  }

  &:not(.absolute) {
    > div:first-child {
      position: relative;

      > div {
        position: absolute;
        height: auto;
        width: auto;
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
        @include responsive-size-xl(60);
        color: v-bind(color);
      }
    }
  }

  &.absolute {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 100;

    > div {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      > div {
        position: absolute;
        height: auto;
        width: auto;
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
        @include responsive-size-xl(60);
        color: $body-color;
      }
    }
  }

  &__content {
    display: grid;
    grid-template-columns: auto;
    justify-content: center;
    align-items: center;
    justify-items: center;
    gap: 10px;
    color: v-bind(color);

    > div:nth-child(2) {
      font-size: 1rem;
      font-weight: 700;

      @media (min-width: map_get($grid-breakpoints, 'lg')) {
        font-size: 1.25rem;
      }
    }
  }
}
</style>
